import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material';
import styled, { CSSObject } from '@emotion/styled';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { QcsTypography } from '../basic/QcsTypography';

const DropZoneContainer = styled.div<{
  isDragOver: boolean;
  hasError: boolean;
  disabled: boolean;
}>(({ isDragOver, hasError, disabled, theme }: any): CSSObject => {
  const palette = theme.palette;
  const borderColor = hasError ? palette.error.main : isDragOver ? palette.primary.main : palette.divider;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '1.25rem 1rem',
    borderRadius: '8px',
    border: `2px dashed ${borderColor}`,
    backgroundColor: isDragOver ? alpha(palette.primary.main, 0.04) : 'transparent',
    transition: 'border-color 0.2s, background-color 0.2s',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    '&:hover': disabled
      ? {}
      : {
          borderColor: palette.primary.main,
          backgroundColor: alpha(palette.primary.main, 0.02),
        },
  };
});

interface Props {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  hasError?: boolean;
}

export const QcsFileDropZone: FC<Props> = ({ onFiles, multiple = true, accept, disabled, hasError }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = () => {
    if (!disabled) fileRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    onFiles(Array.from(files));
    e.target.value = '';
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onFiles(Array.from(files));
      }
    },
    [disabled, onFiles],
  );

  return (
    <>
      <input type="file" multiple={multiple} accept={accept} ref={fileRef} onChange={handleInputChange} hidden />
      <DropZoneContainer
        isDragOver={isDragOver}
        hasError={!!hasError}
        disabled={!!disabled}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: 32,
            color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
            transition: 'color 0.2s',
          }}
        />
        <QcsTypography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
          {t('common.uploadFile')}
        </QcsTypography>
        <QcsTypography variant="caption" sx={{ color: theme.palette.text.disabled }}>
          {t('common.fileMaxSize')}
        </QcsTypography>
      </DropZoneContainer>
    </>
  );
};
